// AdminGrowthPage — guided "click → click → result" growth flow.
// Design: matches the admin light/warm theme (surface #faf8f3, ink near-black, orange accent #ff5b1f).
// Flow: (1) Grow an account [character cards + handle + niche chips → one button] → (2) idea cards →
// (3) make video → inline player + status → (4) mark posted / review → (5) results. No id typing.
import { useState, useEffect, useCallback, useRef } from "react";
import { readyCharacters } from "./growth-picker";
import { ProgressModal } from "@/components/ui/ProgressModal.tsx";
import { useProgress } from "@/components/ui/ProgressContext.tsx";
import { startProgress, atStep, finish, fail, describeRun, type Progress } from "@/components/ui/progress.ts";
import { Link } from "react-router-dom";
import { growthApi, growthQueueApi, type GrowthAccount, type GrowthArc, type ArcVideo, type ArcVideoEdit, type ArcScore } from "../../api/growth.ts";
import { ConceptFrames } from "./ConceptFrameGrid.tsx";
import { adminApi, type CharacterListItem } from "../../api/admin.ts";
import { researchApi, type ShopCategory } from "../../api/research.ts";
import { castingApi, type CastingCandidate } from "../../api/casting.ts";
import { RunThumbs } from "@/components/RunThumbs.tsx";

// ── theme tokens (match the existing pages) ──
const INK = "#0a0a0a", MUTE = "#6b6b6b", LINE = "#e9e6df", CREAM = "#faf8f3", ACCENT = "#ff5b1f";
const card: React.CSSProperties = { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 12, padding: 16 };
const btn = (primary = false): React.CSSProperties => ({
  padding: "9px 16px", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer",
  border: primary ? "none" : `1px solid ${LINE}`, background: primary ? ACCENT : "#fff",
  color: primary ? "#fff" : INK,
});
const chip = (on: boolean): React.CSSProperties => ({
  padding: "6px 12px", borderRadius: 999, fontSize: 13, cursor: "pointer", userSelect: "none",
  border: `1px solid ${on ? ACCENT : LINE}`, background: on ? "#fff6f1" : "#fff", color: on ? ACCENT : INK,
});

// friendly labels — never show raw pattern/format ids to the operator
const PATTERN_LABEL: Record<string, string> = {
  open_loop: "Day-by-day story", story_series: "Cliffhanger series", before_after: "Before / after",
  pity: "Heartfelt story", simp: "Character appeal", niche_switch: "New-audience switch",
  caption_card: "Caption card",
};
// Each video-type (pattern) supports exactly one format ("either" = any). Used to keep the Video type
// picker in sync with the Format selector so an incompatible combo can't be chosen.
const PATTERN_FORMAT: Record<string, "video" | "carousel" | "image" | "either"> = {
  open_loop: "video", story_series: "video", simp: "video",
  before_after: "carousel", niche_switch: "carousel",
  caption_card: "image", pity: "either",
};
const patternFitsFormat = (p: string, fmt: "" | "video" | "carousel" | "image") =>
  !fmt || PATTERN_FORMAT[p] === fmt || PATTERN_FORMAT[p] === "either";
const patternLabel = (p: string) => PATTERN_LABEL[p] ?? p.replace(/_/g, " ");

const MODE_BADGE: Record<ArcVideo["mode"], string> = { video: "🎬", carousel: "🎠", image: "🖼" };
const MODE_LABEL: Record<ArcVideo["mode"], string> = { video: "video", carousel: "carousel", image: "image" };

/** "3 videos · 2 carousels · 1 image" — the format mix across every item in a set of arcs. */
function formatMix(arcs: GrowthArc[]): string {
  const counts: Record<ArcVideo["mode"], number> = { video: 0, carousel: 0, image: 0 };
  for (const a of arcs) for (const v of a.videos) counts[v.mode]++;
  const parts = (Object.keys(counts) as Array<ArcVideo["mode"]>)
    .filter((m) => counts[m] > 0)
    .map((m) => `${counts[m]} ${MODE_LABEL[m]}${counts[m] === 1 ? "" : "s"}`);
  return parts.join(" · ") || "no items yet";
}

// (Character type: use the real `CharacterListItem` from api/admin.ts, not a local
// hand-copied subset — a local copy is exactly how previewUrl silently went missing
// from this page's cards despite the API always returning it.)

export function AdminGrowthPage() {
  const [accounts, setAccounts] = useState<GrowthAccount[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    growthApi.listAccounts().then((r) => setAccounts(r.accounts)).catch((e) => setErr(String(e)));  // catch-reason: the error is shown to the person (setErr)
  }, []);
  useEffect(load, [load]);

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto", color: INK }}>
      <h1 style={{ fontSize: 22, fontWeight: 700 }}>Audience Growth</h1>
      <p style={{ color: MUTE, fontSize: 13, marginTop: 2 }}>
        Grow a TikTok account's following. Pick a character, choose what it's about, and turn ideas into videos.
      </p>
      {err && <div style={{ ...card, color: "#b91c1c", marginTop: 12 }}>{err}</div>}

      {!selected && <GrowAccount accounts={accounts} onCreated={(id) => { load(); setSelected(id); }} onOpen={setSelected} onDeleted={load} />}
      {selected && <AccountWorkspace accountId={selected} onBack={() => { setSelected(null); load(); }} />}
    </div>
  );
}

// ── STEP 1: guided create + the account list ──
// ShopCategoryBreadcrumb — the L1 → L2 TikTok Shop category picker (real crawled
// taxonomy, research_shop_categories) as an alternative to the curated niche chips
// above. Level 1 renders first; picking one fans out its level-2 children below it
// (same breadcrumb shape already used elsewhere in the admin panel, e.g. "Art ›
// Digital Art" on the Manage Niches list) — exactly the "level 2 fans out from the
// level 1 selection" interaction asked for. Only two levels deep, per spec; the
// tree goes to L3 but this picker deliberately doesn't surface it.
function ShopCategoryBreadcrumb({ niche, onSelect }: { niche: string; onSelect: (v: string) => void }) {
  const [l1, setL1] = useState<ShopCategory[] | null>(null);
  const [l1Err, setL1Err] = useState(false);
  const [selectedL1, setSelectedL1] = useState<ShopCategory | null>(null);
  const [l2, setL2] = useState<ShopCategory[] | null>(null);
  const [l2Err, setL2Err] = useState<string | null>(null);
  const [l2Busy, setL2Busy] = useState(false);
  const [selectedL2, setSelectedL2] = useState<ShopCategory | null>(null);

  useEffect(() => {
    researchApi.shopCategories(1).then(setL1).catch(() => setL1Err(true));  // catch-reason: the category picker is optional: hidden when it can't be read (documented below)
  }, []);

  const pickL1 = (cat: ShopCategory) => {
    setSelectedL1(cat);
    setSelectedL2(null);
    setL2(null);
    setL2Err(null);
    setL2Busy(true);
    // (tf3-31, A17) a failed read is shown, never "no subcategories"
    researchApi.shopCategories(2, cat.categoryId)
      .then(setL2)
      .catch((e) => setL2Err(String((e as Error)?.message ?? e)))   // catch-reason: the error is shown under the category
      .finally(() => setL2Busy(false));
  };

  const pickL2 = (cat: ShopCategory) => {
    setSelectedL2(cat);
    onSelect(cat.name);   // niche is a plain string — the L2 category name IS the topic
  };

  // Not seeded yet / crawler hasn't run — degrade quietly rather than show a dead
  // control (same pattern as Finish & Post's music picker falling back when its
  // catalogue is unreachable). The chips above still work either way.
  if (l1Err || (l1 && l1.length === 0)) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, color: MUTE, marginBottom: 6 }}>or pick from TikTok Shop categories</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: selectedL1 ? 8 : 0 }}>
        {!l1 && <span style={{ color: MUTE, fontSize: 13 }}>Loading categories…</span>}
        {l1?.map((c) => (
          <span key={c.categoryId} onClick={() => pickL1(c)} style={chip(selectedL1?.categoryId === c.categoryId)}>
            {c.name}
          </span>
        ))}
      </div>
      {selectedL1 && (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, paddingLeft: 14, borderLeft: `2px solid ${LINE}` }}>
          <span style={{ fontSize: 12, color: MUTE }}>{selectedL1.name} ›</span>
          {l2Busy && <span style={{ color: MUTE, fontSize: 13 }}>Loading…</span>}
          {l2Err && <span style={{ color: "#b91c1c", fontSize: 13 }}>The sub-categories couldn't be read: {l2Err}</span>}
          {!l2Busy && l2?.length === 0 && <span style={{ color: MUTE, fontSize: 13 }}>No sub-categories</span>}
          {l2?.map((c) => (
            <span key={c.categoryId} onClick={() => pickL2(c)} style={chip(selectedL2?.categoryId === c.categoryId)}>
              {c.name}
            </span>
          ))}
        </div>
      )}
      {niche && selectedL2 && (
        <div style={{ fontSize: 11, color: MUTE, marginTop: 6 }}>Selected: {selectedL1?.name} › {selectedL2.name}</div>
      )}
    </div>
  );
}

function GrowAccount({ accounts, onCreated, onOpen, onDeleted }: {
  accounts: GrowthAccount[] | null; onCreated: (id: string) => void; onOpen: (id: string) => void; onDeleted: () => void;
}) {
  const { track } = useProgress();
  const [nicheVocab, setNicheVocab] = useState<Array<{ key: string; label: string }>>([]);
  const [handle, setHandle] = useState("");
  const [charId, setCharId] = useState("");
  const [niche, setNiche] = useState("");
  // Two-level audience: Level 1 = age band + sex (the demographic to grow/target); Level 2 = the
  // niche/category picked below (optional). Both ride the generate call → a stable, distinct audience.
  const [ageBand, setAgeBand] = useState<string>("");
  const [sex, setSex] = useState<string>("");
  const [speaks, setSpeaks] = useState(false);   // Phase C: talking-head vs faceless (default faceless)
  const [patternId, setPatternId] = useState<string>("");   // optional video-type override ("" = director chooses)
  const [format, setFormat] = useState<"" | "video" | "carousel" | "image">("");   // optional format override ("" = auto)
  const AGE_BANDS = ["18-24", "25-34", "35-44", "45-54", "55+", "teen", "adult", "any"];
  const SEXES: Array<{ v: string; label: string }> = [{ v: "F", label: "Female" }, { v: "M", label: "Male" }];
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The niche-filtered roster (tf-041 — character selection now comes AFTER niche,
  // filtered by an LLM call on the same attribute catalog the Characters page's
  // "Cast an arc" already scores against). Re-fetched every time niche changes.
  const [roster, setRoster] = useState<CastingCandidate[] | null>(null);
  const [rosterBusy, setRosterBusy] = useState(false);
  const [rosterNote, setRosterNote] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);          // escape hatch: ignore the filter
  const [allCharacters, setAllCharacters] = useState<CharacterListItem[] | null>(null);

  useEffect(() => {
    adminApi.listNiches().then((r: { niches: Array<{ key: string; label: string }> }) => setNicheVocab(r.niches)).catch(() => {});
  }, []);

  useEffect(() => {
    // Character selection needs SOMETHING to filter on. With a niche → persona-inferred roster (forNiche).
    // With only a demographic (age/sex, no category) → the full roster (browse), since audience ageBand
    // (18-24…) and casting ageRange (18-25…) are different enums and can't be cleanly mapped; gender-only
    // ranking would drop the age signal anyway, so the operator picks from all ready characters. With
    // NOTHING selected → prompt to choose.
    const hasDemographic = !!(ageBand || sex);
    if (!niche && !hasDemographic) { setRoster(null); setCharId(""); return; }
    setCharId("");      // selection changed — the previous pick's fit no longer applies
    if (!niche) {
      // demographic-only: show the full roster (no niche to infer a persona from)
      setRosterBusy(true); setRosterNote(null);
      adminApi.browseCharacters({ limit: 200 })
        .then((r) => { setRoster(r.characters as unknown as CastingCandidate[]); setRosterNote("Showing all characters — pick one for this audience."); })
        .catch((e) => { setErr(String(e)); setRoster([]); })
        .finally(() => setRosterBusy(false));
      return;
    }
    setRosterBusy(true);
    setRosterNote(null);
    castingApi.forNiche(niche)
      .then((r) => {
        setRoster(r.roster);
        // Surface the fallback plainly rather than silently: either the LLM call
        // failed (degrades to the unfiltered roster) or it confidently inferred no
        // filter at all (a gender/age-neutral niche) — both are fine, both worth a note.
        if (r.ignored.some((m) => m.startsWith("persona inference unavailable"))) {
          setRosterNote("Couldn't reach the persona inference — showing the full roster.");
        } else if (!r.neededPersona.gender && !r.neededPersona.ageRange && !r.neededPersona.style && !r.neededPersona.ethnicity) {
          setRosterNote("This niche doesn't skew toward a particular look — showing the full roster, best matches first.");
        }
      })
      .catch((e) => { setErr(String(e)); setRoster([]); })
      .finally(() => setRosterBusy(false));
  }, [niche, ageBand, sex]);

  useEffect(() => {
    if (showAll && !allCharacters) {
      adminApi.browseCharacters({ limit: 200 }).then((r) => setAllCharacters(r.characters)).catch((e) => setErr(String(e)));  // catch-reason: the error is shown to the person (setErr)
    }
  }, [showAll, allCharacters]);

  const ready = !!(charId && (niche || ageBand || sex));
  const createAndGenerate = async () => {
    if (!ready) return;
    setBusy(true); setErr(null);
    try {
      // Handle is optional now: if blank, auto-name a draft account (unique, renameable later).
      const finalHandle = handle.trim() || `draft-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
      const { id } = await track("Creating the account", growthApi.createAccount({ handle: finalHandle, characterRegistryId: charId }),
        { steps: ["Create the account", niche ? "Add its topic" : "Done"], detail: `@${finalHandle}` });
      if (niche) await track("Adding the topic", growthApi.addNiche(id, { niche, mode: "blend" }), { detail: niche });      // blend = silent default; skip when no topic chosen (empty niche → 400)
      await track("Writing the first ideas", growthApi.generateArcs(id, {
        count: 3,
        // Level 1 audience (optional). Level 2 (category) rides as `niche`, already added above.
        ...(ageBand ? { ageBand } : {}),
        ...(sex ? { gender: sex } : {}),
        ...(niche ? { category: niche } : {}),
        ...(speaks ? { speaks: true } : {}),   // Phase C: operator's talking-head choice
        ...(patternId ? { patternId } : {}),   // optional video-type override (else director picks)
        ...(format ? { format } : {}),   // optional format override (else director picks)
      }), { steps: ["Send the request", "The model writes 3 ideas (usually 30–90 s)", "Done"], detail: "writing ideas for this audience…",
        done: (r) => `${(r as { generated?: number }).generated ?? 0} idea(s) ready` }); // kick off ideas; workspace shows them (errors surface below, not swallowed)
      onCreated(id);
    } catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };

  // Both sources render through the same card shape — CastingCandidate carries a
  // score/matched explanation, the plain browse list doesn't; the card just omits
  // that row when absent, so one render path covers the filtered AND override views.
  // (owner, 2026-10-05) only characters with a photo and a name are offered (growth-picker.ts); the rest are counted
  const { ready: cardsFrom, hidden: hiddenCards } = readyCharacters<CastingCandidate | CharacterListItem>(showAll ? (allCharacters ?? []) : (roster ?? []));

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ ...card }}>
        <div style={{ fontWeight: 700, marginBottom: 12 }}>Grow a new account</div>

        {/* audience Level 1 — age band + sex. The demographic to grow toward (broad) or, with a
            category below, to target for shop (narrow). Optional: leave blank to let the director
            infer from the topic, as before. */}
        <div style={{ fontSize: 12, color: MUTE, marginBottom: 6 }}>1. Who's it for?</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
          {AGE_BANDS.map((a) => (
            <span key={a} onClick={() => setAgeBand(ageBand === a ? "" : a)} style={chip(ageBand === a)}>{a}</span>
          ))}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {SEXES.map((s) => (
            <span key={s.v} onClick={() => setSex(sex === s.v ? "" : s.v)} style={chip(sex === s.v)}>{s.label}</span>
          ))}
        </div>

        {/* Phase C: talking-head vs faceless. Default faceless; when on, produce HARD-FAILS if the
            character's avatar won't resolve (surfaced in observability), so it's a deliberate choice. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          <span onClick={() => setSpeaks(!speaks)} style={chip(speaks)}>
            {speaks ? "🗣 character speaks (talking head)" : "faceless (default)"}
          </span>
        </div>

        {/* niche chips (the curated character-topic-fit vocabulary — fast, always available) */}
        <div style={{ fontSize: 12, color: MUTE, marginBottom: 6 }}>2. What's it about?</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          {nicheVocab.map((n) => (
            <span key={n.key} onClick={() => setNiche(n.key)} style={chip(niche === n.key)}>{n.label}</span>
          ))}
          {nicheVocab.length === 0 && <span style={{ color: MUTE, fontSize: 13 }}>Loading topics…</span>}
        </div>
        {/* TikTok Shop category breadcrumb — a more specific alternative sourced from the
            real crawled taxonomy (research_shop_categories) rather than the curated list
            above. Selecting a level-2 category sets the SAME `niche` value the chips do
            (niche is a plain string, not an enum — either source works identically), so
            this is additive: pick a chip for a quick broad topic, or drill into the real
            Shop tree for something specific. Whichever was clicked last wins. */}
        <ShopCategoryBreadcrumb niche={niche} onSelect={setNiche} />

        {/* Optional FORMAT override. Default (Auto) = the director picks image/carousel/video per idea.
            Pick one to force every generated arc to that format. Constrains the video-type list below
            to patterns compatible with the format. */}
        <div style={{ fontSize: 12, color: MUTE, marginBottom: 6 }}>Format <span style={{ color: "#9ca3af" }}>(optional — default: auto)</span></div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          <span onClick={() => setFormat("")} style={chip(format === "")}>Auto (director picks)</span>
          {(["image", "carousel", "video"] as const).map((f) => (
            <span key={f} onClick={() => {
              const next = format === f ? "" : f;
              setFormat(next);
              // keep the pickers consistent: drop a video-type that doesn't fit the new format
              if (patternId && !patternFitsFormat(patternId, next)) setPatternId("");
            }} style={chip(format === f)}>
              {f === "image" ? "🖼 Image" : f === "carousel" ? "🎠 Carousel" : "🎬 Video"}
            </span>
          ))}
        </div>

        {/* Optional video-type OVERRIDE. Default (Auto) = the director picks the pattern per arc.
            Pick one to force every generated arc to that type. `simp`/Character appeal is rejected by
            the backend for teen/young-kids audiences (the one content-safety exclusion) — the error
            surfaces below if that happens. */}
        <div style={{ fontSize: 12, color: MUTE, marginBottom: 6 }}>Video type <span style={{ color: "#9ca3af" }}>(optional — default: auto{format ? ` · showing ${format}-compatible` : ""})</span></div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
          <span onClick={() => setPatternId("")} style={chip(patternId === "")}>Auto (director picks)</span>
          {Object.keys(PATTERN_LABEL).filter((p) => patternFitsFormat(p, format)).map((p) => (
            <span key={p} onClick={() => setPatternId(patternId === p ? "" : p)} style={chip(patternId === p)}>{PATTERN_LABEL[p]}</span>
          ))}
        </div>

        {/* character cards — NOW comes after niche, filtered by it. This is the point of
            growth per its own tagline: grow the account FOR that niche's audience, so the
            presenter should already fit before a human even looks at faces. */}
        <div style={{ fontSize: 12, color: MUTE, marginBottom: 6 }}>
          3. Choose the character its videos star
          {niche && !showAll && <span style={{ color: "#9ca3af" }}> — filtered for "{niche}"</span>}
        </div>
        {(!niche && !ageBand && !sex) ? (
          <div style={{ color: MUTE, fontSize: 13, marginBottom: 16 }}>Pick who it's for (or a topic) above to see characters.</div>
        ) : rosterBusy && !showAll ? (
          <div style={{ color: MUTE, fontSize: 13, marginBottom: 16 }}>{niche ? `Finding who fits "${niche}"…` : "Loading characters…"}</div>
        ) : cardsFrom.length === 0 ? (
          <div style={{ color: "#b45309", marginBottom: 16 }}>
            {hiddenCards ? `No ready characters — ${hiddenCards} need a photo or a name on the Characters page first.` : "No characters yet — create one on the Characters page first."}
          </div>
        ) : (
          <>
            {rosterNote && !showAll && <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>{rosterNote}</div>}
            {hiddenCards > 0 && <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>
              {hiddenCards} hidden — no photo or no name yet (fix them on the Characters page)</div>}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 8, maxHeight: 220, overflowY: "auto", marginBottom: 8 }}>
              {cardsFrom.map((c) => {
                const on = charId === c.stockId;
                const scored = "score" in c ? c : null;
                return (
                  <button key={c.stockId} onClick={() => setCharId(c.stockId)}
                    style={{ textAlign: "left", padding: 10, borderRadius: 10, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 8,
                      border: `1px solid ${on ? ACCENT : LINE}`, background: on ? "#fff6f1" : "#fff" }}>
                    {/* Thumbnail — small + round so a dense picker grid stays scannable; a
                        plain initial-letter circle when a character has no preview yet
                        (pending/failed state) rather than a broken image or blank box. */}
                    {c.previewUrl ? (
                      <img src={c.previewUrl} alt="" width={36} height={36}
                        style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: "#eee" }} />
                    ) : (
                      <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: "#eee",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: MUTE }}>
                        {(c.displayName || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {c.displayName}
                      </div>
                      <div style={{ fontSize: 11, color: MUTE, marginTop: 2 }}>
                        {c.gender || "—"}{on ? " · selected" : ""}
                        {scored && scored.matched.length > 0 && <span> · fits: {scored.matched.join(", ")}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
        {niche && (
          <div style={{ marginBottom: 16 }}>
            <span onClick={() => setShowAll(!showAll)} style={{ fontSize: 12, color: MUTE, textDecoration: "underline", cursor: "pointer" }}>
              {showAll ? "← back to filtered matches" : "not seeing who you want? browse everyone →"}
            </span>
          </div>
        )}

        {/* handle */}
        <div style={{ fontSize: 12, color: MUTE, marginBottom: 6 }}>4. The TikTok handle you'll post to <span style={{ color: "#9ca3af" }}>(optional — auto-named if blank, rename later)</span></div>
        <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="yourhandle"
          style={{ width: "100%", maxWidth: 320, padding: "9px 12px", borderRadius: 8, border: `1px solid ${LINE}`, marginBottom: 16, fontSize: 14 }} />


        {err && <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <button disabled={!ready || busy} onClick={createAndGenerate}
          style={{ ...btn(true), opacity: !ready || busy ? 0.5 : 1 }}>
          {busy ? "Setting up…" : "Create & generate ideas"}
        </button>
        {!ready && <span style={{ color: MUTE, fontSize: 12, marginLeft: 10 }}>Pick a character and a topic (or audience) to continue.</span>}
      </div>

      {/* existing accounts */}
      {accounts && accounts.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Your accounts</div>
          <div style={{ display: "grid", gap: 8 }}>
            {accounts.map((a) => (
              <div key={a.id}
                style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div onClick={() => onOpen(a.id)} style={{ cursor: "pointer", flex: 1 }}>
                  <span style={{ fontWeight: 600 }}>@{a.handle}</span>
                  <span style={{ color: MUTE, fontSize: 12, marginLeft: 8 }}>{a.platform}</span>
                </div>
                <span onClick={() => onOpen(a.id)} style={{ color: ACCENT, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Open →</span>
                <button
                  onClick={async () => {
                    if (!confirm(`Delete @${a.handle} and ALL its arcs, videos, and runs? This cannot be undone.`)) return;
                    try { await track("Deleting the account", growthApi.deleteAccount(a.id), { detail: `@${a.handle} and all its arcs, videos and runs` }); onDeleted(); }
                    catch (e) { alert("Delete failed: " + String((e as Error).message ?? e)); }
                  }}
                  style={{ border: "1px solid #fca5a5", color: "#b91c1c", background: "#fff", borderRadius: 8, padding: "5px 10px", cursor: "pointer", fontSize: 12 }}>
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── STEPS 2-5: the account workspace (ideas → videos → status → results) ──
function AccountWorkspace({ accountId, onBack }: { accountId: string; onBack: () => void }) {
  const { track } = useProgress();
  const [account, setAccount] = useState<GrowthAccount | null>(null);
  const [arcs, setArcs] = useState<GrowthArc[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [providedAssetIds, setProvidedAssetIds] = useState<string[]>([]);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const load = useCallback(() => {
    growthApi.getAccount(accountId).then((r) => setAccount(r.account)).catch((e) => setErr(String(e)));  // catch-reason: the error is shown to the person (setErr)
    growthApi.listArcs(accountId).then((r) => setArcs(r.arcs)).catch((e) => setErr(String(e)));  // catch-reason: the error is shown to the person (setErr)
  }, [accountId]);
  // (audit) ONE poll for the page while any idea's first frame is being made — not a timer per card.
  const anyMaking = (arcs ?? []).some((a) => a.firstFrame?.status === "making" || a.firstFrame?.status === "queued");
  useEffect(() => { if (!anyMaking) return; const t = setTimeout(load, 4000); return () => clearTimeout(t); }, [anyMaking, arcs, load]);
  useEffect(load, [load]);

  // poll while anything is producing. A terminal orderStatus (failed/cancelled) stops the poll for
  // that item even with no assetUrl — otherwise a failed run would show "Making…" forever, exactly
  // the bug the order-completion webhook (Phase 2 cleanup) was built to fix for the success case.
  useEffect(() => {
    const producing = (arcs ?? []).some((a) => a.videos.some((v) =>
      v.orderId && !v.assetUrl && v.orderStatus !== "failed" && v.orderStatus !== "cancelled"));
    if (producing && !pollRef.current) {
      pollRef.current = window.setInterval(load, 15000);
    } else if (!producing && pollRef.current) {
      window.clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; } };
  }, [arcs, load]);

  // ── the progress modal (instead of a greyed-out button) ───────────────────────────────────────────────────
  const [prog, setProg] = useState<Progress | null>(null);
  const progRef = useRef<Progress | null>(null);
  const show = (p: Progress | null) => { progRef.current = p; setProg(p); };
  /** Follow the runs an action started, by their REAL engine status, until each is at review / done / failed. */
  const followRuns = async (arcId: string, runIds: string[], stepIndex: number) => {
    const settled = new Map<string, string>();
    for (let tick = 0; tick < 150 && settled.size < runIds.length && progRef.current; tick++) {   // up to ~10 min
      const lines: string[] = []; let pctSum = 0, pctN = 0;
      for (const id of runIds) {
        // (tf3-31, A17) a status that can't be read is said on its line (and asked again), never "waiting for the engine"
        const r = settled.has(id) ? { s: null } : await growthQueueApi.runStatus(arcId, id)
          .then((s) => ({ s, readErr: undefined as string | undefined }), (e) => ({ s: null, readErr: String((e as Error)?.message ?? e) }));   // catch-reason: named on the video's line below
        const d = settled.has(id) ? { detail: settled.get(id)!, pct: 100, settled: "done" as const }
          : "readErr" in r && r.readErr ? { detail: `its status couldn't be read (${r.readErr}) — trying again`, pct: null, settled: null } : describeRun(r.s);
        if (d.settled) settled.set(id, d.detail);
        if (d.pct != null) { pctSum += d.pct; pctN++; }
        lines.push(`video ${runIds.indexOf(id) + 1}: ${d.detail}`);
      }
      if (!progRef.current) return;
      show(atStep(progRef.current, stepIndex, lines.join(" · "), pctN ? Math.round(pctSum / runIds.length) : null));
      if (settled.size === runIds.length) break;
      await new Promise((r) => setTimeout(r, 4000));
    }
    if (progRef.current) show(finish(progRef.current, [...settled.values()].join(" · ") || "started — it continues in the background",
      [{ label: "Open the review", href: `/admin/growth/arcs/${arcId}` }]));
  };

  const makeFirstFrameNow = async (arcId: string) => {
    const p = startProgress("Making the mood picture", ["Write the picture's description from the idea", "Generate one image (cheapest model, ~5–20 s)", "Store it"]);
    show(atStep(p, 1, "FLUX.2 klein — one picture, the first video's opening frame"));
    try {
      const r = await growthApi.makeFirstFrame(arcId);
      if (r.status === "failed") show(fail(progRef.current ?? p, "the image model or the upload refused it — try again"));
      else show(finish(progRef.current ?? p, r.status === "making" ? "already being made — it will appear on the card" : "done — it's on the card"));
      load();
    } catch (e) { show(fail(progRef.current ?? p, String(e))); }
  };

  const generateMore = async () => {
    setBusy("gen"); setErr(null);
    let p = startProgress("Generating ideas", ["Send the request", "The model writes the ideas (usually 30–90 s)", "Save them",
      ...(account?.autoFirstFrame ? ["Queue a mood picture for each (cheapest model)"] : [])]);
    show(atStep(p, 1, "writing 3 ideas for this account's audience…"));
    try {
      const r = await growthApi.generateArcs(accountId, { count: 3 });
      p = progRef.current ?? p;
      show(finish(p, `${(r as { generated?: number }).generated ?? 0} new idea(s)${account?.autoFirstFrame ? " — their mood pictures are being made now" : ""}`));
      load();
    } catch (e) { show(fail(progRef.current ?? p, String(e))); setErr(String(e)); } finally { setBusy(null); }
  };
  /** PILOT FIRST: make the rest of an arc — only when you press Continue (after the pilot shows traction). */
  const continueArc = async (arcId: string) => {
    const arc = (arcs ?? []).find((a) => a.id === arcId);
    const rest = (arc?.videos ?? []).filter((v) => !v.orderId).map((v) => v.seq).sort((a, b) => a - b);
    if (!rest.length) return;
    setBusy(arcId); setErr(null);
    const p = startProgress("Continuing the arc", ["Order the rest", "The engine makes them (script → frames → review)"]);
    show(p);
    try {
      const r = await growthApi.produceArc(arcId, { seqs: rest }) as { results?: { ok: boolean; runId?: string; error?: string }[] };
      load();
      const runs = (r.results ?? []).filter((x) => x.ok && x.runId).map((x) => x.runId!);
      const errors = (r.results ?? []).filter((x) => !x.ok).map((x) => x.error ?? "refused");
      if (!runs.length) { show(fail(progRef.current ?? p, errors.join(" · ") || "no order was placed")); return; }
      await followRuns(arcId, runs, 1);
    } catch (e) { show(fail(progRef.current ?? p, String(e))); setErr(String(e)); } finally { setBusy(null); }
  };

  const makeVideo = async (arcId: string) => {
    setBusy(arcId); setErr(null);
    // PILOT FIRST: the arc's FIRST part is made in full; the rest get a cheap first frame each, and are made only when
    // you press Continue on the arc — so an arc proves itself with one video before the rest are paid for.
    const p = startProgress("Making the pilot", ["Approve the idea", "Order part 1 · prepare the rest", "The engine makes part 1 (script → frames → review)"]);
    show(p);
    try {
      // one click: approve (a no-op when approval isn't required, harmless when it is) then produce
      const arc = (arcs ?? []).find((a) => a.id === arcId);
      if (arc && arc.status !== "approved" && arc.status !== "producing") await growthApi.editArc(arcId, { status: "approved" });
      const seqs = [...(arc?.videos ?? [])].map((v) => v.seq).sort((a, b) => a - b);
      const pilot = seqs[0] ?? 0, rest = seqs.slice(1);
      show(atStep(progRef.current ?? p, 1, rest.length ? `ordering part 1; preparing a picture for parts 2–${rest.length + 1}…` : "ordering the video…"));
      const r = await growthApi.produceArc(arcId, { ...(providedAssetIds.length ? { providedAssetIds } : {}), seqs: [pilot] }) as { results?: { ok: boolean; runId?: string; error?: string }[] };
      if (rest.length) void growthApi.partFrames(arcId, rest).catch(() => undefined);   // pictures only — no orders
      load();
      const runs = (r.results ?? []).filter((x) => x.ok && x.runId).map((x) => x.runId!);
      const errors = (r.results ?? []).filter((x) => !x.ok).map((x) => x.error ?? "refused");
      if (!runs.length) { show(fail(progRef.current ?? p, errors.join(" · ") || "no order was placed")); return; }
      await followRuns(arcId, runs, 2);
    } catch (e) { show(fail(progRef.current ?? p, String(e))); setErr(String(e)); } finally { setBusy(null); }
  };
  const runCampaign = async () => {
    setBusy("campaign"); setErr(null);
    const p = startProgress("Running a campaign", ["Send the request", "Write the ideas; order each one's pilot (part 1) and prepare the rest", "Done — the pilots continue in the engine; Continue each arc that gets traction"]);
    show(atStep(p, 1, "writing ideas and placing each one's orders…"));
    try {
      const r = await growthApi.campaign(accountId, providedAssetIds.length ? { providedAssetIds } : undefined) as { campaign?: { results: { ok: boolean; error?: string }[] }[] };
      const all = (r.campaign ?? []).flatMap((c) => c.results);
      const ok = all.filter((x) => x.ok).length, bad = all.filter((x) => !x.ok);
      show(finish(progRef.current ?? p, `${ok} video(s) started${bad.length ? ` · ${bad.length} refused: ${bad.map((b) => b.error).filter(Boolean).slice(0, 3).join(" · ")}` : ""}`,
        [{ label: "Open the review queue", href: "/admin/growth/queue" }]));
      load();
    } catch (e) { show(fail(progRef.current ?? p, String(e))); setErr(String(e)); } finally { setBusy(null); }
  };
  const uploadImages = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setUploadErr(null);
    try {
      const { assetIds } = await track("Uploading the images", growthApi.uploadImages(Array.from(files)),
        { detail: `${files.length} image(s)`, done: (r) => `${r.assetIds.length} image(s) ready to use` });
      setProvidedAssetIds(assetIds);
    } catch (e) { setUploadErr(String(e)); }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <button onClick={onBack} style={{ ...btn(), marginBottom: 16 }}>← All accounts</button>
      <ProgressModal p={prog} onClose={() => show(null)} />
      {account && <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>@{account.handle}</div>}
      {account && (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 10, cursor: "pointer" }}
          title="Makes one mood picture per new idea, with the cheapest image model (FLUX.2 klein). Not the video's frame: the video's frames are approved at the storyboard.">
          <input type="checkbox" checked={!!account.autoFirstFrame} disabled={busy === "ff-toggle"}
            onChange={async (e) => { setBusy("ff-toggle"); try { await track(e.target.checked ? "Turning auto mood picture on" : "Turning auto mood picture off", growthApi.setAccountSettings(accountId, { autoFirstFrame: e.target.checked })); load(); } catch (x) { setErr(String(x)); } finally { setBusy(null); } }} />
          Auto mood picture for new ideas <span style={{ color: MUTE }}>(cheapest model)</span>
        </label>
      )}
      {err && <div style={{ ...card, color: "#b91c1c", marginBottom: 12 }}>{err}</div>}
      {account && <AutopostChannel accountId={accountId} />}

      {/* Phase 3: provide an image set instead of generating one. Optional — everything below works
          without it, generating a keyframe per item the same way it always has. */}
      <div style={{ ...card, marginBottom: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <label style={{ ...btn(), cursor: "pointer" }}>
          Upload image set
          <input type="file" accept="image/*" multiple style={{ display: "none" }}
            onChange={(e) => uploadImages(e.target.files)} />
        </label>
        {providedAssetIds.length > 0 && (
          <span style={{ fontSize: 12, color: MUTE }}>
            {providedAssetIds.length} image{providedAssetIds.length === 1 ? "" : "s"} ready — used for the next
            single-item (carousel/image) idea you make or campaign you run.{" "}
            <span onClick={() => setProvidedAssetIds([])} style={{ color: ACCENT, cursor: "pointer" }}>Clear</span>
          </span>
        )}
        {uploadErr && <span style={{ fontSize: 12, color: "#b91c1c" }}>{uploadErr}</span>}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 8px", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontWeight: 700 }}>Queued content</div>
          {arcs && arcs.length > 0 && <div style={{ fontSize: 12, color: MUTE, marginTop: 2 }}>{formatMix(arcs)}</div>}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button disabled={busy === "gen"} onClick={generateMore} style={btn()}>{busy === "gen" ? "Thinking…" : "Generate more ideas"}</button>
          <button disabled={busy === "campaign"} onClick={runCampaign} style={btn(true)}>
            {busy === "campaign" ? "Running…" : "Run a campaign"}
          </button>
        </div>
      </div>

      {!arcs ? <div style={{ color: MUTE }}>Loading…</div> :
        arcs.length === 0 ? <div style={{ ...card, color: MUTE }}>No ideas yet. Click "Generate more ideas" or "Run a campaign" to get started.</div> : (
        <div style={{ display: "grid", gap: 12 }}>
          {arcs.map((arc) => (
            <IdeaCard key={arc.id} arc={arc} busy={busy === arc.id} onMake={() => makeVideo(arc.id)} onContinue={() => continueArc(arc.id)} onFirstFrame={() => makeFirstFrameNow(arc.id)} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── an idea → its items, with inline preview + status + next action ──
/** (creative-1 C1.2d, B-34; planning 18:10 option A) the idea's MOOD PICTURE — the picture the card leads with; not the
 *  video's frame (the video's frames are approved at the storyboard). Made automatically (the account toggle) or on click. */
/**
 * Session 6 W3: the account's PUBLISHING CHANNEL — what autopilot posts this account's finished parts to (pilot first,
 * cadence-spaced, under the per-account cap). A stalled channel (not linked, disconnected, needs reconnecting, owned by
 * another user) says so HERE — and as an Operations alert — so a series never stalls silently.
 */
function AutopostChannel({ accountId }: { accountId: string }) {
  const [c, setC] = useState<import("../../api/growth.ts").GrowthAccountChannel | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(() => { growthApi.getAccountChannel(accountId).then(setC).catch((e) => setMsg((e as Error).message)); }, [accountId]);  // catch-reason: the error is shown to the person (setMsg)
  useEffect(load, [load]);
  const link = async (id: string | null) => {
    setMsg(null);
    try { await growthApi.linkAccountChannel(accountId, id); load(); } catch (e) { setMsg((e as Error).message); }
  };
  const connect = async (channel: "tiktok" | "youtube") => {
    setMsg(null);
    const popup = window.open("about:blank", "tf-connect-growth", "width=520,height=720");   // opened inside the click
    try {
      const { authorizeUrl } = await growthApi.connectForGrowth(accountId, channel, window.location.href);
      if (popup) popup.location.href = authorizeUrl; else window.location.href = authorizeUrl;
      const t = window.setInterval(() => { if (!popup || popup.closed) { window.clearInterval(t); load(); } }, 1500);
    } catch (e) { popup?.close(); setMsg((e as Error).message); }
  };
  if (!c) return msg ? <div style={{ ...card, marginBottom: 12, color: "#b91c1c" }}>{msg}</div> : null;
  return (
    <div style={{ ...card, marginBottom: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Autopilot posting</div>
      {c.stalled
        ? <div style={{ color: "#b91c1c", fontSize: 13, marginBottom: 6 }}>Stalled — {c.stalled}</div>
        : <div style={{ color: "#15803d", fontSize: 13, marginBottom: 6 }}>Posting to {c.linked!.account_name} ({c.linked!.channel}) — finished parts post in order, spaced by the arc's cadence.</div>}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select aria-label="Publishing channel" value={c.account.social_channel_id ?? ""} onChange={(e) => void link(e.target.value || null)}
          style={{ padding: "3px 6px", border: "1px solid #d1d5db", borderRadius: 6 }}>
          <option value="">— not linked —</option>
          {c.choices.map((ch) => <option key={ch.id} value={ch.id} disabled={ch.status !== "active"}>{ch.account_name} ({ch.channel}{ch.status !== "active" ? `, ${ch.status}` : ""})</option>)}
        </select>
        <button style={btn()} onClick={() => void connect("tiktok")}>Connect TikTok for growth</button>
        <button style={btn()} onClick={() => void connect("youtube")}>Connect YouTube for growth</button>
      </div>
      {msg && <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 6 }}>{msg}</div>}
    </div>
  );
}

function FirstFrame({ arc, onMake }: { arc: GrowthArc; onMake: () => void }) {
  const ff = arc.firstFrame ?? { url: null, status: "none" };
  const box = { width: 96, height: 170, borderRadius: 8, flexShrink: 0, objectFit: "cover" as const, background: "#f3f4f6" };
  if (ff.url && ff.status === "ready") return <img src={ff.url} alt="mood picture" style={box} data-visual />;
  if (ff.status === "making" || ff.status === "queued") return <div style={{ ...box, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: MUTE }} data-visual>making…</div>;
  return (
    <button onClick={onMake}
      style={{ ...box, border: "1px dashed #d1d5db", cursor: "pointer", fontSize: 11, color: MUTE }} data-visual
      title="One picture to help judge this idea, with the cheapest image model. Not the video's frame: the video's frames are approved at the storyboard">
      {ff.status === "failed" ? "failed — try again" : "＋ mood picture"}
    </button>
  );
}

function IdeaCard({ arc, busy, onMake, onContinue, onFirstFrame, onChanged }: { arc: GrowthArc; busy: boolean; onMake: () => void; onContinue: () => void; onFirstFrame: () => void; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const anyProduced = arc.videos.some((v) => v.orderId);
  const waiting = arc.videos.filter((v) => !v.orderId).length;   // parts prepared, not yet made
  const modes = [...new Set(arc.videos.map((v) => v.mode))];
  return (
    <div style={{ ...card }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <FirstFrame arc={arc} onMake={onFirstFrame} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12, color: ACCENT, fontWeight: 600 }}>
            {modes.map((m) => MODE_BADGE[m]).join(" ")} {patternLabel(arc.patternId)}
          </div>
          <div style={{ fontWeight: 600, marginTop: 2 }}>{arc.premise || "Untitled idea"}</div>
          {arc.payoff && <div style={{ fontSize: 13, color: MUTE, marginTop: 2 }}>→ {arc.payoff}</div>}
        </div>
        {!anyProduced && (
          <div style={{ display: "flex", gap: 6, alignItems: "center", whiteSpace: "nowrap" }}>
            {/* Phase 5.3 — pre-production review + approve before producing. Review
                opens the per-video review surface; Approve marks the arc producible
                (required when GROWTH_REQUIRE_APPROVAL is on). */}
            <Link to={`/admin/growth/arcs/${arc.id}`} style={{ ...btn(), textDecoration: "none", fontSize: 12, padding: "5px 10px" }}>Review →</Link>
            <button disabled={busy} onClick={onMake} style={{ ...btn(true), opacity: busy ? 0.5 : 1 }}>
              {busy ? "Making…" : "Make it"}
            </button>
          </div>
        )}
      </div>

      {/* PILOT FIRST: once part 1 is made, the rest wait — with its traction beside the button that makes them */}
      {anyProduced && waiting > 0 && <PilotBar arc={arc} waiting={waiting} busy={busy} onContinue={onContinue} />}

      {/* Concept-frame grid — the cheapest-tier visuals, shown inline right after generate so the
          operator can see + regenerate + approve each image WITHOUT clicking into the arc. Only before
          production (once produced, the items go through the storyboard/final flow). */}
      {!anyProduced && <div style={{ marginTop: 12 }}><ConceptFrames arcId={arc.id} /></div>}

      <button onClick={() => setOpen(!open)} style={{ ...btn(), marginTop: 10, fontSize: 12, padding: "5px 10px" }}>
        {open ? "Hide" : "See"} the {formatMix([arc])}
      </button>

      {open && (
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          {[...arc.videos].sort((a, b) => a.seq - b.seq).map((v) => (
            <ItemRow key={v.seq} arcId={arc.id} v={v} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

function statusOf(v: ArcVideo): { label: string; color: string } {
  if (v.postedAt) return { label: "Posted", color: "#16a34a" };
  if (v.orderStatus === "failed") return { label: "Failed — try again", color: "#b91c1c" };
  if (v.orderStatus === "cancelled") return { label: "Cancelled", color: MUTE };
  if (v.assetUrl) return { label: "Ready to post", color: ACCENT };
  if (v.orderId) return { label: "Making… (holds for your review)", color: "#2563eb" };
  if (v.firstFrame?.status === "making") return { label: "Preparing a picture…", color: MUTE };
  if (v.firstFrame?.url) return { label: "Prepared — made when you Continue", color: MUTE };
  return { label: "Not started", color: MUTE };
}

/** Phase 3: the structured item, labelled — the words split onto the beats (video), each slide's words +
 *  what it shows (carousel), the scene / words / caption brief (image). Nothing for an unstructured item. */
function StructuredItem({ v }: { v: ArcVideo }) {
  const row = (label: string, text: string | null | undefined) => text ? (
    <div style={{ display: "flex", gap: 6 }}><span style={{ color: MUTE, minWidth: 74 }}>{label}</span><span>{text}</span></div>
  ) : null;
  const box = { margin: "8px 0 0", fontSize: 12, display: "grid", gap: 3 } as const;
  if (v.mode === "video" && v.scriptParts)
    return <div style={box}>{row("Hook", v.scriptParts.hook)}{v.scriptParts.bodyParagraphs.map((b, i) => <div key={i}>{row(`Beat ${i + 1}`, b)}</div>)}{row("CTA", v.scriptParts.cta)}</div>;
  if (v.mode === "carousel" && v.slideItems)
    return <ol style={{ ...box, paddingLeft: 18 }}>{v.slideItems.map((s, i) => <li key={i}><b>{s.onFrameText}</b> <span style={{ color: MUTE }}>— shows: {s.visualHint}</span></li>)}</ol>;
  if (v.mode === "image" && v.scene)
    return <div style={box}>{row("Scene", v.scene.sceneBrief)}{row("On image", v.scene.onFrameText)}{row("Caption", v.scene.captionBrief)}</div>;
  return null;
}

/** Phase 3: edit an item's words BEFORE production (the backend closes edits once it has an order). The
 *  backend validates the edit to the item's format (e.g. the body-beat count) and names what's wrong. */
function ItemWordsEditor({ arcId, v, onSaved }: { arcId: string; v: ArcVideo; onSaved: () => void }) {
  const { track } = useProgress();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ArcVideoEdit>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const start = () => {
    setErr(null);
    setDraft(v.scriptParts ? { scriptParts: { ...v.scriptParts, bodyParagraphs: [...v.scriptParts.bodyParagraphs] } }
      : v.slideItems ? { slideItems: v.slideItems.map((x) => ({ ...x })) }
      : v.scene ? { scene: { ...v.scene } } : {});
    setOpen(true);
  };
  const save = async () => {
    setBusy(true); setErr(null);
    try { await track("Saving the words", growthApi.editArcVideo(arcId, v.seq, draft), { detail: `video ${v.seq + 1}` }); setOpen(false); onSaved(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const input = { width: "100%", fontSize: 12, padding: 4, border: `1px solid ${LINE}`, borderRadius: 6, boxSizing: "border-box" } as const;
  const field = (label: string, value: string, set: (t: string) => void, multiline = false) => (
    <label style={{ display: "grid", gap: 2, fontSize: 12 }}><span style={{ color: MUTE }}>{label}</span>
      {multiline ? <textarea rows={2} style={input} value={value} onChange={(e) => set(e.target.value)} />
        : <input style={input} value={value} onChange={(e) => set(e.target.value)} />}</label>
  );
  if (!open) return <button onClick={start} style={{ marginTop: 8, fontSize: 12 }}>Edit words</button>;
  const sp = draft.scriptParts, si = draft.slideItems, sc = draft.scene;
  return (
    <div style={{ marginTop: 8, display: "grid", gap: 6, padding: 8, background: CREAM, borderRadius: 8 }}>
      {sp && <>
        {sp.hook !== null && field("Hook", sp.hook, (t) => setDraft({ scriptParts: { ...sp, hook: t } }))}
        {sp.bodyParagraphs.map((b, i) => <div key={i}>{field(`Beat ${i + 1}`, b, (t) => setDraft({ scriptParts: { ...sp, bodyParagraphs: sp.bodyParagraphs.map((x, j) => (j === i ? t : x)) } }), true)}</div>)}
        {sp.cta !== null && field("CTA", sp.cta, (t) => setDraft({ scriptParts: { ...sp, cta: t } }))}
      </>}
      {si && si.map((x, i) => <div key={i} style={{ display: "grid", gap: 4, gridTemplateColumns: "1fr 1fr" }}>
        {field(`Slide ${i + 1} words`, x.onFrameText, (t) => setDraft({ slideItems: si.map((y, j) => (j === i ? { ...y, onFrameText: t } : y)) }))}
        {field("shows", x.visualHint, (t) => setDraft({ slideItems: si.map((y, j) => (j === i ? { ...y, visualHint: t } : y)) }))}
      </div>)}
      {sc && <>
        {field("Scene (what the image shows)", sc.sceneBrief, (t) => setDraft({ scene: { ...sc, sceneBrief: t } }), true)}
        {field("Words on the image (blank = none)", sc.onFrameText ?? "", (t) => setDraft({ scene: { ...sc, onFrameText: t.trim() ? t : null } }))}
        {field("Caption brief", sc.captionBrief, (t) => setDraft({ scene: { ...sc, captionBrief: t } }), true)}
      </>}
      {err && <div style={{ color: "#b91c1c", fontSize: 12 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={save} disabled={busy} style={{ fontSize: 12 }}>{busy ? "Saving…" : "Save"}</button>
        <button onClick={() => setOpen(false)} disabled={busy} style={{ fontSize: 12 }}>Cancel</button>
      </div>
    </div>
  );
}

/** One authored item, rendered by its mode: video → player, carousel → slide grid, image → frame.
 *  Status/poll/mark-posted work identically across modes (all derive from orderId/assetUrl/postedAt). */
function ItemRow({ arcId, v, onChanged }: { arcId: string; v: ArcVideo; onChanged: () => void }) {
  const { track } = useProgress();
  const [busy, setBusy] = useState(false);
  const [postLink, setPostLink] = useState("");   // (creative-1) the post's link: its numbers are then read
  const s = statusOf(v);
  const markPosted = async () => { setBusy(true); try { await track("Marking it posted", growthApi.markPosted(arcId, v.seq, undefined, postLink.trim() || undefined), { detail: `video ${v.seq + 1}` }); onChanged(); } catch { /* shown in the window */ } finally { setBusy(false); } };
  const slideUrls = v.mode === "carousel" ? (v.assetUrls ?? (v.assetUrl ? [v.assetUrl] : [])) : [];

  return (
    <div style={{ border: `1px solid ${LINE}`, borderRadius: 10, padding: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        {/* a part not yet made shows its prepared first frame (pilot first) — the arc is seen before it's paid for */}
        {!v.orderId && v.firstFrame?.url && (
          <img src={v.firstFrame.url} alt={`part ${v.seq + 1} mood picture`} loading="lazy"
            style={{ width: 62, height: 110, objectFit: "cover", borderRadius: 6, flex: "0 0 auto", background: "#f3f4f6" }} />
        )}
        <div style={{ fontSize: 13, minWidth: 0, flex: 1 }}>
          <span style={{ color: MUTE }}>
            {MODE_BADGE[v.mode]} {v.role === "hit" ? "Payoff" : `Part ${v.seq + 1}`}:{" "}
          </span>
          {/* Phase 3: the structured item's first line; the flat text only for an unstructured item */}
          {v.mode === "video" && (v.scriptParts ? (v.scriptParts.hook ?? v.scriptParts.bodyParagraphs[0]) : v.script)}
          {v.mode === "image" && (v.scene ? v.scene.sceneBrief : v.caption)}
          {v.mode === "carousel" && (v.slideItems?.length ? `${v.slideItems.length} slides: ${v.slideItems[0].onFrameText}`
            : v.slides?.length ? `${v.slides.length} slides: ${v.slides[0]}` : "carousel")}
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: s.color, whiteSpace: "nowrap" }}>{s.label}</span>
      </div>

      {/* Phase 3: the structured item — what the growth order carries — and its editor (pre-production only) */}
      <StructuredItem v={v} />
      {!v.orderId && (v.scriptParts || v.slideItems || v.scene) && <ItemWordsEditor arcId={arcId} v={v} onSaved={onChanged} />}

      {/* carousel's per-slide beats (an unstructured item — structured ones are shown above) */}
      {v.mode === "carousel" && !v.slideItems && v.slides && v.slides.length > 0 && (
        <ol style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12, color: MUTE }}>
          {v.slides.map((slide, i) => <li key={i}>{slide}</li>)}
        </ol>
      )}

      {/* finished preview, per mode */}
      {v.mode === "video" && v.assetUrl && (
        <div style={{ marginTop: 10 }}>
          {/* Phase 4b: the finished video (Finish & Post) when there is one — the version to post. */}
          <video src={v.postUrl ?? v.assetUrl} controls style={{ width: "100%", maxWidth: 240, borderRadius: 8, background: "#000" }} />
        </div>
      )}
      {v.mode === "image" && v.assetUrl && (
        <div style={{ marginTop: 10 }}>
          <img src={v.assetUrl} style={{ width: "100%", maxWidth: 240, borderRadius: 8 }} />
        </div>
      )}
      {v.mode === "carousel" && slideUrls.length > 0 && (
        <div style={{ marginTop: 10, display: "flex", gap: 6, overflowX: "auto" }}>
          {slideUrls.map((url, i) => (
            <img key={i} src={url} style={{ height: 160, borderRadius: 8, flexShrink: 0 }} />
          ))}
        </div>
      )}

      {/* the engine's post caption (still/carousel) — what actually goes in the caption field */}
      {v.postCaption && (
        <div style={{ marginTop: 8, fontSize: 12 }}>
          <div style={{ color: MUTE, marginBottom: 2 }}>Post caption</div>
          {v.postCaption.hook && <div style={{ fontWeight: 600 }}>{v.postCaption.hook}</div>}
          {v.postCaption.body && <div>{v.postCaption.body}</div>}
          {v.postCaption.cta && <div>{v.postCaption.cta}</div>}
          {/* hashtags come from a JSON column — render only a real array */}
          {Array.isArray(v.postCaption.hashtags) && v.postCaption.hashtags.length > 0 && (
            <div style={{ color: MUTE }}>{v.postCaption.hashtags.join(" ")}</div>
          )}
        </div>
      )}

      {/* actions once something finished */}
      {v.assetUrl && (
        <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {!v.postedAt && <input value={postLink} onChange={(e) => setPostLink(e.target.value)} placeholder="Link to the post (optional)"
            aria-label="Link to the post" style={{ flex: "1 1 220px", minWidth: 0, padding: "7px 10px", borderRadius: 8, border: `1px solid ${LINE}`, fontSize: 13 }} />}
          {!v.postedAt && <button disabled={busy} onClick={markPosted} style={btn(true)}>{busy ? "…" : "Mark posted"}</button>}
          <a href={v.assetUrl} download style={{ ...btn(), textDecoration: "none", display: "inline-block" }}>Download</a>
        </div>
      )}

      {/* producing → its PICTURES as they're made (not only its script), and a link to THIS part's run — the link
          used to open the Runs page in general, with nothing saying which run was this part's (2026-09-29). A part's
          orderId IS its engine run id — every producer stores `setVideoOrder(arcId, seq, r.runId)`. */}
      {v.orderId && !v.assetUrl && (
        <div style={{ marginTop: 10 }}>
          <RunThumbs runId={v.orderId} max={6} height={110} onOpen={() => { window.location.href = `/admin/runs?run=${v.orderId}`; }} />
          {s.label.startsWith("Making") && (
            <div style={{ marginTop: 6, fontSize: 12, color: MUTE }}>
              This is being made and will pause for your review. Review it in{" "}
              <a href={`/admin/runs?run=${v.orderId}`} style={{ color: ACCENT }}>its run →</a>, then it finishes here.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** PILOT FIRST: the arc's traction after part 1 — followers gained since it was posted — beside the one button that
 *  makes the rest. Nothing is made automatically; the operator decides with the number in front of them. */
function PilotBar({ arc, waiting, busy, onContinue }: { arc: GrowthArc; waiting: number; busy: boolean; onContinue: () => void }) {
  const [score, setScore] = useState<ArcScore | null | undefined>(undefined);
  const [scoreErr, setScoreErr] = useState<string | null>(null);
  const [measureErr, setMeasureErr] = useState<string | null>(null);
  const [measuring, setMeasuring] = useState(false);
  const posted = arc.videos.some((v) => v.postedAt);
  // (tf3-31, A17) getArcScore throws on a failed read (only a 404 is "not measured"): shown, never "not measured yet"
  useEffect(() => { let alive = true; setScoreErr(null); if (posted) void growthApi.getArcScore(arc.id).then((s) => alive && setScore(s))
    .catch((e) => alive && setScoreErr(String((e as Error)?.message ?? e))); else setScore(null); return () => { alive = false; }; }, [arc.id, posted]);   // catch-reason: shown on the pilot bar
  // (tf3-32) a failed measure is its own message, cleared on the next try — never "not measured"
  const measure = async () => { setMeasuring(true); setMeasureErr(null); try { const r = await growthApi.measureArc(arc.id); setScore(r.score); setScoreErr(null); }
    catch (e) { setMeasureErr(String((e as Error)?.message ?? e)); } finally { setMeasuring(false); } };   // catch-reason: shown on the pilot bar
  const f = score?.followersGained;
  return (
    <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "#f8fafc", border: `1px solid ${LINE}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
      <div style={{ fontSize: 13 }}>
        <b>Pilot</b>{" · "}
        {!posted ? <span style={{ color: MUTE }}>post part 1 and mark it posted to see its traction</span>
          : scoreErr ? <span style={{ color: "#b91c1c" }}>its traction couldn't be read: {scoreErr} <button onClick={measure} disabled={measuring} style={{ ...btn(), fontSize: 12, padding: "2px 8px" }}>{measuring ? "Measuring…" : "Measure now"}</button></span>
          : score === undefined ? <span style={{ color: MUTE }}>reading traction…</span>
          : f == null ? <span style={{ color: MUTE }}>not measured yet — <button onClick={measure} disabled={measuring} style={{ ...btn(), fontSize: 12, padding: "2px 8px" }}>{measuring ? "Measuring…" : "Measure now"}</button></span>
          : <span><b style={{ color: f > 0 ? "#16a34a" : MUTE }}>{f > 0 ? "+" : ""}{f} followers</b> since part 1 was posted
              <span style={{ color: MUTE }}> ({score?.windowFrom ? new Date(score.windowFrom).toLocaleDateString() : ""} → now)</span>{" "}
              <button onClick={measure} disabled={measuring} style={{ ...btn(), fontSize: 11, padding: "1px 6px" }}>{measuring ? "…" : "refresh"}</button></span>}
        {measureErr && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 2 }}>Measuring failed: {measureErr}</div>}
        <div style={{ fontSize: 12, color: MUTE, marginTop: 2 }}>{waiting} more part{waiting === 1 ? "" : "s"} prepared — made only when you press Continue.</div>
      </div>
      <button disabled={busy} onClick={onContinue} style={{ ...btn(true), opacity: busy ? 0.5 : 1 }}>{busy ? "Ordering…" : `Continue — make the other ${waiting}`}</button>
    </div>
  );
}
